// Shared Fuel & Loss extractor — pure, React-free, runs in the browser AND the Node compute Lambda.
// The stock-movement reconciliation (massBalanceCompute.ts) closes to ~0 by construction, which hid the
// refinery's REAL fuel/flare/loss. That accounting already exists in the customer's own workbook — we just
// weren't reading it. This module reads it, for BOTH dataset formats, into one common FuelLossReport:
//   • OAS / EET  → the "MTD" master sheet (Own/Total Fuel, Unaccounted Loss, Flare, BS&W, feed+product yields,
//                  and the historical monthly loss trend). Verified vs EET's own numbers (Jun 2,881.73 /
//                  Jul 8,122.46 MT unaccounted loss).
//   • AORA / Toledo → the Volume/Mass production sheets' "Loss/Gain" line (Total Charges − Total Yields),
//                  reported on both a volume and a mass basis.
import * as XLSX from 'xlsx';

export interface FuelLossRow {
  name: string;
  opening?: number | null; receipt?: number | null; delivery?: number | null;
  closing?: number | null; consumption?: number | null; production?: number | null; yieldPct?: number | null;
}
export interface MonthlyLoss {
  month: string; totalLoss: number | null; crudeTP: number | null; lossPct: number | null;
  flare: number | null; fuel: number | null; fuelPct: number | null; bsw: number | null;
}
export interface LossGainBasis {
  charges: number | null; yields: number | null; lossGain: number | null; lossGainPct: number | null; unit: string;
}
export interface FuelLossReport {
  source: 'oas' | 'aora';
  period: string;
  units: string;                     // 'MT' for OAS; 'Lbs/Bbls' for AORA
  // headline (OAS populates all; AORA populates crudeProcessed via charges + loss from mass basis)
  crudeProcessed: number | null;
  ownFuel: number | null;
  naturalGas: number | null;
  totalFuel: number | null;
  fuelPct: number | null;            // fraction, e.g. 0.07 = 7 %
  unaccountedLoss: number | null;
  lossPct: number | null;
  flare: number | null;
  bsw: number | null;
  fnlPct: number | null;
  // detail
  feed: FuelLossRow[];
  products: FuelLossRow[];
  subBalances: { label: string; mtd: number | null; ftd: number | null }[];
  monthlyTrend: MonthlyLoss[];
  basis: { volume: LossGainBasis | null; mass: LossGainBasis | null } | null;  // AORA only
  /** Own Fuel split by stream (Refinery Fuel Gas / Coke / Fuel Oil), in MT. Only the condensed MT
   *  export carries it, so it is absent for periods published in the older layout. These sum to a
   *  little LESS than the MTD "Own Fuel" figure -- 57,787 vs 58,370 on 2026-09 -- so the UI shows
   *  the shortfall rather than implying they reconcile. */
  fuelComponents?: { sap: string; name: string; mass: number }[];
}

// ---------- helpers ----------
const S = (v: any): string => (v == null ? '' : String(v).trim());
const NUM = (v: any): number | null => {
  if (typeof v === 'number') return isNaN(v) ? null : v;
  if (v == null) return null;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return isNaN(n) ? null : n;
};
const rowsOf = (ws: XLSX.WorkSheet): any[][] =>
  XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, blankrows: false, raw: true });

// find a cell exactly equal to `label`, return the first numeric cell to its right
function labelValue(rows: any[][], label: string): number | null {
  const lab = label.trim().toLowerCase();
  for (const row of rows) {
    for (let i = 0; i < row.length; i++) {
      if (S(row[i]).toLowerCase() === lab) {
        for (let j = i + 1; j < row.length; j++) { const n = NUM(row[j]); if (n != null) return n; }
      }
    }
  }
  return null;
}
// first row where every `must` term matches some cell (exact or substring)
function findHeader(rows: any[][], must: string[]): { ri: number; header: any[] } | null {
  const m = must.map((x) => x.toLowerCase());
  for (let ri = 0; ri < rows.length; ri++) {
    const cells = rows[ri].map((c) => S(c).toLowerCase());
    if (m.every((term) => cells.some((cc) => cc === term || cc.includes(term)))) return { ri, header: rows[ri] };
  }
  return null;
}
function colIndex(header: any[], name: string): number | null {
  const n = name.toLowerCase();
  for (let i = 0; i < header.length; i++) if (S(header[i]).toLowerCase() === n) return i;
  for (let i = 0; i < header.length; i++) if (S(header[i]).toLowerCase().includes(n)) return i;
  return null;
}
function parseTable(rows: any[][], hdrRi: number, header: any[], cols: Record<string, string>, stop = 'total'): FuelLossRow[] {
  const ci: Record<string, number | null> = {};
  for (const k of Object.keys(cols)) ci[k] = colIndex(header, cols[k]);
  const out: FuelLossRow[] = [];
  for (let r = hdrRi + 1; r < rows.length; r++) {
    const row = rows[r];
    const nameIdx = ci['name'];
    const nm = nameIdx != null ? S(row[nameIdx]) : '';
    if (nm.toLowerCase() === stop) break;
    if (!nm) continue;
    const rec: any = { name: nm };
    for (const k of Object.keys(ci)) { if (k === 'name' || ci[k] == null) continue; rec[k] = NUM(row[ci[k] as number]); }
    out.push(rec);
  }
  return out;
}

// ---------- OAS / EET : the "MTD" master sheet ----------
export function parseFuelLossOas(wb: XLSX.WorkBook, period: string): FuelLossReport | null {
  const name = wb.SheetNames.find((n) => n.trim().toUpperCase() === 'MTD');
  if (!name) return null;
  const rows = rowsOf(wb.Sheets[name]);

  const rep: FuelLossReport = {
    source: 'oas', period, units: 'MT',
    crudeProcessed: labelValue(rows, 'Crude+HSGO Processed'),
    naturalGas: labelValue(rows, 'Natural Gas'),
    ownFuel: labelValue(rows, 'Own Fuel'),
    totalFuel: labelValue(rows, 'Total Fuel'),
    unaccountedLoss: labelValue(rows, 'Unaccounted LOSS'),
    fuelPct: labelValue(rows, 'Fuel %'),
    lossPct: labelValue(rows, 'Loss%'),
    fnlPct: labelValue(rows, 'F&L% with NG'),
    flare: null, bsw: null,
    feed: [], products: [], subBalances: [], monthlyTrend: [], basis: null,
  };

  const fh = findHeader(rows, ['Feed', 'Opening', 'Consumption']);
  if (fh) rep.feed = parseTable(rows, fh.ri, fh.header,
    { name: 'Feed', opening: 'Opening', receipt: 'Receipt', delivery: 'Delivery', closing: 'Closing', consumption: 'Consumption' });

  const ph = findHeader(rows, ['Product', 'Opening', 'Production']);
  if (ph) rep.products = parseTable(rows, ph.ri, ph.header,
    { name: 'Product', opening: 'Opening', receipt: 'Receipt', delivery: 'Delivery', closing: 'Closing', production: 'Production', yieldPct: '%' });

  // fenceline / tank sub-balances ("Description | MTD in Tons | FTD in Tons")
  const sh = findHeader(rows, ['Description', 'MTD in Tons']);
  if (sh) {
    const dCol = colIndex(sh.header, 'Description'), mCol = colIndex(sh.header, 'MTD in Tons'), fCol = colIndex(sh.header, 'FTD in Tons');
    for (let r = sh.ri + 1; r < rows.length; r++) {
      const lbl = dCol != null ? S(rows[r][dCol]) : '';
      if (!lbl) continue;
      const mtd = mCol != null ? NUM(rows[r][mCol]) : null;
      if (mtd == null && !lbl) continue;
      rep.subBalances.push({ label: lbl, mtd, ftd: fCol != null ? NUM(rows[r][fCol]) : null });
      if (rep.subBalances.length >= 6) break;
    }
  }

  // historical monthly loss trend ("Month | Total Loss | Crude TP | %Loss | Flare | Fuel | Fuel % | BS&W")
  const hh = findHeader(rows, ['Month', 'Total Loss', 'Flare', 'BS&W']);
  if (hh) {
    const ci = {
      month: colIndex(hh.header, 'Month'), totalLoss: colIndex(hh.header, 'Total Loss'), crudeTP: colIndex(hh.header, 'Crude TP'),
      lossPct: colIndex(hh.header, '%Loss'), flare: colIndex(hh.header, 'Flare'), fuel: colIndex(hh.header, 'Fuel'),
      fuelPct: colIndex(hh.header, 'Fuel %'), bsw: colIndex(hh.header, 'BS&W'),
    };
    for (let r = hh.ri + 1; r < rows.length; r++) {
      const mv = ci.month != null ? rows[r][ci.month] : null;
      let monthLabel = '';
      if (mv instanceof Date) monthLabel = `${mv.getFullYear()}-${String(mv.getMonth() + 1).padStart(2, '0')}`;
      else if (['total', 'ytd'].includes(S(mv).toLowerCase())) monthLabel = S(mv);
      else continue;
      rep.monthlyTrend.push({
        month: monthLabel,
        totalLoss: ci.totalLoss != null ? NUM(rows[r][ci.totalLoss]) : null,
        crudeTP: ci.crudeTP != null ? NUM(rows[r][ci.crudeTP]) : null,
        lossPct: ci.lossPct != null ? NUM(rows[r][ci.lossPct]) : null,
        flare: ci.flare != null ? NUM(rows[r][ci.flare]) : null,
        fuel: ci.fuel != null ? NUM(rows[r][ci.fuel]) : null,
        fuelPct: ci.fuelPct != null ? NUM(rows[r][ci.fuelPct]) : null,
        bsw: ci.bsw != null ? NUM(rows[r][ci.bsw]) : null,
      });
    }
    const cur = rep.monthlyTrend.find((m) => m.month === period);
    if (cur) { rep.flare = cur.flare; rep.bsw = cur.bsw; }
  }
  return rep;
}

// ---------- AORA / Toledo : the Volume/Mass "Loss/Gain" line ----------
function parseLossGainSheet(ws: XLSX.WorkSheet): LossGainBasis | null {
  const rows = rowsOf(ws);
  // Monthly Production Volume/Mass + % sit in the last two numeric pairs of each summary row.
  const rowFor = (label: string): any[] | null => {
    for (const row of rows) if (row.some((c) => S(c).toLowerCase().includes(label))) return row;
    return null;
  };
  const monthlyPair = (row: any[] | null): { val: number | null; pct: number | null } => {
    if (!row) return { val: null, pct: null };
    const nums: Array<{ i: number; v: number }> = [];
    for (let i = 0; i < row.length; i++) { const n = NUM(row[i]); if (n != null) nums.push({ i, v: n }); }
    if (nums.length < 2) return { val: null, pct: null };
    // last numeric = monthly %, second-to-last = monthly volume/mass
    return { val: nums[nums.length - 2].v, pct: nums[nums.length - 1].v };
  };
  const unit = rows.slice(0, 8).flat().some((c) => /Lbs/i.test(S(c))) ? 'Lbs' : (rows.slice(0, 8).flat().some((c) => /Bbls/i.test(S(c))) ? 'Bbls' : '');
  const charges = monthlyPair(rowFor('total charges'));
  const yieldsR = monthlyPair(rowFor('total yields'));
  const lg = monthlyPair(rowFor('loss/gain'));
  if (charges.val == null && lg.val == null) return null;
  return { charges: charges.val, yields: yieldsR.val, lossGain: lg.val, lossGainPct: lg.pct, unit };
}

export function parseFuelLossAora(wb: XLSX.WorkBook, period: string): FuelLossReport | null {
  const volName = wb.SheetNames.find((n) => n.trim().toLowerCase() === 'volume');
  const massName = wb.SheetNames.find((n) => n.trim().toLowerCase() === 'mass');
  if (!volName && !massName) return null;
  const volume = volName ? parseLossGainSheet(wb.Sheets[volName]) : null;
  const mass = massName ? parseLossGainSheet(wb.Sheets[massName]) : null;
  if (!volume && !mass) return null;
  return {
    source: 'aora', period, units: 'Lbs/Bbls',
    // Mass basis is the physically meaningful loss; surface it as the headline loss.
    crudeProcessed: mass?.charges ?? volume?.charges ?? null,
    ownFuel: null, naturalGas: null, totalFuel: null, fuelPct: null,
    unaccountedLoss: mass?.lossGain ?? null,
    lossPct: mass?.lossGainPct != null ? mass.lossGainPct / 100 : null,
    flare: null, bsw: null, fnlPct: null,
    feed: [], products: [], subBalances: [], monthlyTrend: [],
    basis: { volume, mass },
  };
}

// ---------- dispatcher ----------
export function detectFuelLossSource(wb: XLSX.WorkBook): 'oas' | 'aora' | null {
  const names = wb.SheetNames.map((n) => n.trim().toUpperCase());
  if (names.includes('MTD')) return 'oas';
  if (names.includes('VOLUME') && names.includes('MASS')) return 'aora';
  return null;
}
export function parseFuelLoss(wb: XLSX.WorkBook, period: string, source?: 'oas' | 'aora'): FuelLossReport | null {
  const src = source || detectFuelLossSource(wb);
  if (src === 'oas') return parseFuelLossOas(wb, period);
  if (src === 'aora') return parseFuelLossAora(wb, period);
  return null;
}
